import { Psbt } from '@exodus/bitcoinjs';
import { InvalidInputError, UnauthorizedError, InternalError, } from '@exodus/web3-errors';
import { approve, connect } from '@exodus/web3-rpc-handlers';
import { createEmptySimulationResult } from '@exodus/web3-utils';
import { parseAndValidateGetAddressOptions, parseAndValidateSendBtcTransactionOptions, parseAndValidateSignMessageOptions, parseAndValidateSignTransactionOptions, } from './parseAndValidateJWTs.js';
import { stringToNumber } from './utils.js';
const NETWORK = 'bitcoin';
export function exposeHandlers(rpc, deps) {
    const { isTrusted, assetName, ensureTrusted, approveConnection, approveTransactions, approveMessage, isAutoApproved, getIsConnected, ensureUnlocked = async () => true, getAccounts, getActiveWalletAccountData, transactionSigner, messageSigner, getOrigin, getPathname, scanDomains, getAsset, txSend, broadcastTx, } = deps;
    async function assertTrusted() {
        const trusted = await isTrusted();
        if (!trusted) {
            throw new UnauthorizedError();
        }
    }
    const convertToIndexToAddressRecord = (inputsToSign) => {
        /*
         [{
          address: "33TKH4kkiFPyTLDNmdNsLggyLeAYre57Qm",
          signingIndexes: [1],
          sigHash: 131
         }]
          =>
          {
           1: { address: '33TKH4kkiFPyTLDNmdNsLggyLeAYre57Qm', sigHash: 131 }
          }
         */
        return inputsToSign.reduce((accumulator, currentValue) => {
            currentValue.signingIndexes.forEach((index) => (accumulator[index] = {
                address: currentValue.address,
                sigHash: Number.isInteger(currentValue.sigHash)
                    ? currentValue.sigHash
                    : 0,
            }));
            return accumulator;
        }, Object.create(null));
    };
    function exposeFunction(name, 
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fn) {
        return rpc.exposeFunction(`${NETWORK}_${name}`, fn);
    }
    /**
     * Wallet Standard Handlers
     */
    exposeFunction('standard_connect', async ({ purposes, }) => {
        await connect({
            approveConnection,
            ensureTrusted,
            isTrusted,
            ensureUnlocked,
            getOrigin,
            getPathname,
            scanDomains,
        }, NETWORK);
        return getAccounts({ purposes });
    });
    /**
     * Sats Connect API Handlers
     */
    exposeFunction('connect', async (request) => {
        const { purposes } = parseAndValidateGetAddressOptions(request);
        await connect({
            approveConnection,
            ensureTrusted,
            isTrusted,
            ensureUnlocked,
            getOrigin,
            getPathname,
            scanDomains,
        }, NETWORK);
        return getAccounts({ purposes, publicKeyEncoding: 'hex' });
    });
    exposeFunction('signTransaction', async (request) => {
        await assertTrusted();
        const signTransactionOptions = parseAndValidateSignTransactionOptions(request);
        const indexToAddressRecord = convertToIndexToAddressRecord(signTransactionOptions.inputsToSign);
        // Need to get a list of wallet accounts below.
        await ensureUnlocked();
        const walletAccounts = await getAccounts({
            purposes: ['payment', 'ordinals'],
        });
        const asset = await getAsset(NETWORK);
        const simulatedChanges = await asset.api.web3.simulateTransactions({
            transactionBuffers: [
                Buffer.from(signTransactionOptions.psbtBase64, 'base64'),
            ],
            indexToAddressRecord,
            walletAddresses: walletAccounts.reduce((acc, account) => {
                if (!account) {
                    return acc;
                }
                acc[account.address] = true;
                return acc;
            }, Object.create(null)),
        });
        await approve(approveTransactions, isAutoApproved, NETWORK, simulatedChanges);
        const walletAccount = await getActiveWalletAccountData();
        const { plainTx } = await transactionSigner.signTransaction({
            baseAssetName: assetName,
            unsignedTx: {
                txData: {
                    psbtBuffer: Buffer.from(signTransactionOptions.psbtBase64, 'base64'),
                },
                txMeta: {
                    inputsToSign: indexToAddressRecord,
                    addressPathsMap: signTransactionOptions.inputsToSign.reduce((accumulator, currentValue) => {
                        // ToDo: double check if it's actually always a constant
                        accumulator[currentValue.address] = 'm/0/0';
                        return accumulator;
                    }, Object.create(null)),
                },
            },
            walletAccount,
        });
        if (signTransactionOptions.broadcast) {
            const psbt = Psbt.fromBuffer(plainTx.rawPSBT);
            const transaction = psbt.finalizeAllInputs().extractTransaction();
            await broadcastTx(transaction.toHex());
            return { txId: transaction.getId() };
        }
        return { psbtBase64: plainTx.rawPSBT.toString('base64') };
    });
    exposeFunction('sendBtcTransaction', async (request) => {
        await assertTrusted();
        if (typeof txSend !== 'function') {
            throw new InternalError();
        }
        const sendBtcTransactionOptions = parseAndValidateSendBtcTransactionOptions(request);
        const { recipients, senderAddress } = sendBtcTransactionOptions;
        if (recipients.length === 0) {
            return { txid: null };
        }
        if (recipients.length > 1) {
            throw new InvalidInputError('Sending BTC to more than 1 recipient is not supported.');
        }
        // Need to get a list of wallet accounts below.
        await ensureUnlocked();
        const accounts = await getAccounts({
            purposes: ['payment'],
        });
        if (accounts[0]?.address !== senderAddress) {
            throw new InvalidInputError('Sending BTC is allowed only from a payment address.');
        }
        const asset = await getAsset(NETWORK);
        // Assets API doesn't expose a method to create a transfer PSBT/unsignedTx that is a required parameter for simulation.
        // Creating a dummy simulated result to provide it to the approval function.
        const simulatedChanges = createEmptySimulationResult({ asset });
        const amountNumber = stringToNumber(recipients[0].amountSats);
        const amount = asset.currency.baseUnit(amountNumber);
        simulatedChanges.balanceChanges.willSend.push({
            balance: amount,
        });
        await approve(approveTransactions, isAutoApproved, NETWORK, simulatedChanges);
        const walletAccount = await getActiveWalletAccountData();
        const { txId } = await txSend({
            assetName: asset.name,
            walletAccount,
            receiver: {
                address: recipients[0].address,
                amount,
            },
        });
        return { txid: txId };
    });
    exposeFunction('signMessage', async (request) => {
        await assertTrusted();
        const { address, message } = parseAndValidateSignMessageOptions(request);
        const walletAccount = await getActiveWalletAccountData();
        await approve(approveMessage, async () => false, NETWORK, message);
        const accounts = await getAccounts({
            purposes: ['payment', 'ordinals'],
            returnPurposeNumbers: true,
        });
        const purpose = accounts.find((account) => address === account?.address)?.purposeNumber;
        if (!purpose) {
            throw new InvalidInputError('Non-existing address');
        }
        const signedMessage = await messageSigner.signMessage({
            baseAssetName: assetName,
            walletAccount,
            message: {
                bip322Message: { message: Buffer.from(message), address },
            },
            purpose,
        });
        return signedMessage.toString('base64');
    });
    exposeFunction('isConnected', async () => {
        if (!getIsConnected) {
            return null;
        }
        return getIsConnected();
    });
}
